import { ReactNode, Children, isValidElement, ReactElement } from 'react';
import Title from '../compounds/title';
import Navigation from '../compounds/navigation';
import Actions from '../compounds/actions';
import Primary from '../compounds/primary';
import Secondary from '../compounds/secondary';

export interface ExtractedChildren {
  titleChild: ReactNode | null;
  navLinks: ReactNode[];
  primaryButton: ReactNode | null;
  secondaryButton: ReactNode | null;
  hasNavLinks: boolean;
  hasActions: boolean;
}

/**
 * Helper to find a child component by type
 * Returns null if not found (better for React - null doesn't render)
 */
const findChildByType = (
  children: ReactNode[],
  componentType: unknown,
): ReactElement | null => {
  return (
    children.find(
      (child): child is ReactElement =>
        isValidElement(child) && child.type === componentType,
    ) || null
  );
};

/**
 * Helper to extract children from a container component
 */
const extractChildrenFromContainer = (
  container: ReactElement | undefined,
): ReactNode[] => {
  return container ? Children.toArray(container.props.children) : [];
};

/**
 * Extract specific child types from compound component children
 * Separates Title, Navigation links, and Action buttons
 */
export const extractChildren = (children: ReactNode): ExtractedChildren => {
  const childArray = Children.toArray(children);

  // Find main container components
  const titleChild = findChildByType(childArray, Title);
  const navigationChild = findChildByType(childArray, Navigation);
  const actionsChild = findChildByType(childArray, Actions);

  // Extract navigation links
  const navLinks = extractChildrenFromContainer(navigationChild);

  // Extract action buttons
  const actionChildren = extractChildrenFromContainer(actionsChild);
  const primaryButton = findChildByType(actionChildren, Primary);
  const secondaryButton = findChildByType(actionChildren, Secondary);

  return {
    titleChild,
    navLinks,
    primaryButton,
    secondaryButton,
    hasNavLinks: navLinks.length > 0,
    hasActions: Boolean(primaryButton || secondaryButton),
  };
};
